import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Globe2,
  Award,
  Building2,
  GraduationCap,
  Users,
  Briefcase,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  Lock,
  ScanLine,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TierBadge from "@/components/TierBadge";

export default function Landing() {
  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="landing-page">
      <Navbar />
      <Hero />
      <TrustStrip />
      <CareerJourney />
      <HowItWorks />
      <WhyEmployers />
      <ScoreBreakdownSection />
      <PublicPassportDemo />
      <WhyProfessionals />
      <ScoreSection />
      <VerificationEcosystem />
      <InstitutionsSection />
      <GigWorkers />
      <Testimonials />
      <FaqSection />
      <CtaBanner />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow">
            <span className="w-1.5 h-1.5 bg-ec-emerald rounded-full" />
            Professional Credibility Infrastructure
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6">
            Your Professional <br />
            <span className="relative inline-block">
              Passport
              <svg
                className="absolute -bottom-2 left-0 w-full"
                viewBox="0 0 300 12"
                preserveAspectRatio="none"
              >
                <path
                  d="M2 8 Q 75 2 150 6 T 298 4"
                  fill="none"
                  stroke="#059669"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>{" "}
            for Life.
          </h1>
          <p className="text-lg sm:text-xl text-ec-ink-soft mt-7 max-w-2xl leading-relaxed">
            <span className="font-semibold text-ec-ink">Build a career reputation that follows you everywhere.</span>{" "}
            Verified Experience. Verified Conduct. Verified Recommendations. EthicCred unites every
            employer, institution and credential you've ever earned into a single, globally
            verifiable digital identity.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-register">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2">
                Claim Your EthicCred ID <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/verify" data-testid="hero-cta-verify">
              <Button variant="outline" className="rounded-sm h-12 border-ec-ink px-6 text-base gap-2 hover:bg-ec-mute">
                <ScanLine className="w-4 h-4" /> Verify a Passport
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl border-t border-ec-line pt-6">
            <Stat n="2.4M+" l="Professionals" />
            <Stat n="38K+" l="Organizations" />
            <Stat n="194" l="Countries" />
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          data-testid="landing-hero-gif-wrap"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_ethictrust-build/artifacts/lasu7jes_ethiccred%20gif.gif"
            alt="EthicCred — your professional passport for life"
            className="w-full h-auto select-none"
            draggable="false"
            data-testid="landing-hero-gif"
          />
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-3xl font-black tracking-tight">{n}</div>
      <div className="eyebrow mt-1">{l}</div>
    </div>
  );
}

function TrustStrip() {
  const logos = ["GLOBAL HR FORUM", "EUROHIRE", "TALENTBANK", "PROVERIFY", "CRED NETWORK", "INSTITUTIONS+"];
  return (
    <div className="border-y border-ec-line bg-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-wrap items-center justify-between gap-y-4">
        <span className="eyebrow">Trusted by hiring teams at</span>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-3 opacity-70">
          {logos.map((l) => (
            <span key={l} className="font-display text-sm font-bold tracking-widest">
              {l}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", icon: ShieldCheck, t: "Claim your EthicCred ID", d: "Sign up in 60 seconds and get a unique, global Professional ID and digital passport." },
    { n: "02", icon: Briefcase, t: "Get experiences verified", d: "Employers, institutions and clients issue signed certificates directly to your passport." },
    { n: "03", icon: Award, t: "Build a Credibility Score", d: "Every verified record contributes to a tamper-proof 0–1000 EthicCred Score." },
    { n: "04", icon: Globe2, t: "Share globally, instantly", d: "One QR code or one link — anyone can verify your record in seconds." },
  ];
  return (
    <section id="how" className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
        <div className="md:col-span-5">
          <div className="eyebrow">How EthicCred works</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            A passport you actually own.
          </h2>
        </div>
        <p className="md:col-span-5 md:col-start-7 text-ec-ink-soft text-base leading-relaxed">
          We are building the credibility layer of the global workforce. Records are issued by
          verified institutions, cryptographically signed, and follow you for life — across every
          job, country and career.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-ec-line">
        {steps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className={`p-8 ${i < 3 ? "md:border-r md:border-ec-line" : ""} ${
              i < steps.length - 1 ? "border-b md:border-b-0 border-ec-line" : ""
            } hover:bg-ec-soft transition-colors`}
          >
            <div className="flex items-center justify-between">
              <span className="font-mono-plex text-xs text-ec-ink-soft">{s.n}</span>
              <s.icon className="w-5 h-5 text-ec-emerald" />
            </div>
            <h3 className="font-display text-xl font-bold mt-6">{s.t}</h3>
            <p className="text-ec-ink-soft text-sm mt-2 leading-relaxed">{s.d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function WhyEmployers() {
  const items = [
    { icon: ShieldCheck, t: "Zero fake experiences", d: "Every employment record is signed by the issuing organization. Forgery is mathematically impossible." },
    { icon: ScanLine, t: "Verify in under 5 seconds", d: "Scan a QR or paste an EthicCred ID — get the full verified history instantly." },
    { icon: Lock, t: "Audit-ready compliance", d: "Tamper-proof logs, consent receipts, and exportable evidence for every check." },
    { icon: Users, t: "Reduce time-to-hire 40%", d: "Stop chasing references. The reference comes with the candidate." },
  ];
  return (
    <section id="why" className="bg-ec-soft border-y border-ec-line">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="eyebrow">Why employers trust EthicCred</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Background checks, <br />reinvented.
            </h2>
            <p className="text-ec-ink-soft mt-5 leading-relaxed">
              The world's biggest hiring teams use EthicCred to replace expensive third-party
              verification with cryptographic certainty.
            </p>
            <Link to="/register?role=organization" className="inline-block mt-8" data-testid="why-employers-cta">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2">For Employers <ArrowUpRight className="w-4 h-4" /></Button>
            </Link>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-ec-line border border-ec-line">
            {items.map((it, i) => (
              <motion.div
                key={it.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white p-8"
              >
                <it.icon className="w-6 h-6 text-ec-emerald" />
                <h3 className="font-display text-xl font-bold mt-4">{it.t}</h3>
                <p className="text-sm text-ec-ink-soft mt-2 leading-relaxed">{it.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhyProfessionals() {
  const features = [
    "Verified employment, education & internships",
    "Conduct & character certificates",
    "Skill certifications backed by issuers",
    "Recommendations from real managers",
    "A Credibility Score that grows with you",
    "QR-based instant sharing — no PDFs",
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <div className="eyebrow">Why professionals love it</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            Own your career story. Forever.
          </h2>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            Your resume disappears the day you leave a job. Your EthicCred Passport doesn't. Carry
            every verified experience — across every employer, country and decade.
          </p>
          <ul className="mt-8 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-ec-emerald mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2">
          {/* Mock passport card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative bg-ec-soft border border-ec-ink/15 p-8 noise-overlay shadow-[0_30px_80px_-20px_rgba(0,0,0,0.2)]"
          >
            <div className="absolute inset-0 pointer-events-none opacity-[0.04] text-[100px] font-display font-black flex items-center justify-center select-none">
              ETHICCRED
            </div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div>
                  <div className="eyebrow">Professional Passport</div>
                  <h3 className="font-display text-3xl font-black mt-2">P***** S***********</h3>
                  <p className="text-ec-ink-soft text-sm">Senior Product Designer · B*****</p>
                </div>
                <div className="text-right">
                  <div className="eyebrow">EthicCred ID</div>
                  <div className="font-mono-plex font-bold text-base mt-1">AAYY11YYXX</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-ec-emerald border border-ec-emerald/30 bg-ec-emerald/10 px-2 py-0.5 rounded-full">
                    Score · 842 · Trusted
                  </div>
                </div>
              </div>
              <div className="mt-8 border-t border-dashed border-ec-ink/15 pt-6 grid grid-cols-2 gap-6">
                <PassportRow label="Verified employments" v="6" />
                <PassportRow label="Verified education" v="2" />
                <PassportRow label="Certifications" v="11" />
                <PassportRow label="Recommendations" v="8" />
              </div>
              <div className="absolute right-0 bottom-[-10px] stamp text-ec-emerald">Verified</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function PassportRow({ label, v }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="font-display font-semibold text-lg mt-1">{v}</div>
    </div>
  );
}

function ScoreSection() {
  const factors = [
    { label: "Verified Employment", weight: "30%" },
    { label: "Verified Education", weight: "16%" },
    { label: "Certifications & Training", weight: "13%" },
    { label: "Recommendations", weight: "10%" },
    { label: "Internships & Volunteer", weight: "10%" },
    { label: "Conduct Certificates", weight: "6%" },
    { label: "Skills & Achievements", weight: "10%" },
    { label: "Career Consistency", weight: "5%" },
  ];
  return (
    <section className="bg-ec-ink text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-zinc-400">The EthicCred Score</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              A trust score for the global workforce.
            </h2>
            <p className="text-zinc-400 mt-5 leading-relaxed">
              The EthicCred Score is a 0–1000 measure of verified professional credibility. It can
              never be manually edited — every point is earned from cryptographically verified
              records.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-xs eyebrow text-ec-emerald border border-ec-emerald/30 bg-ec-emerald/10 px-3 py-1.5 rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> Updated in real time
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
              {factors.map((f) => (
                <div key={f.label} className="bg-ec-ink p-6 flex items-center justify-between">
                  <span className="text-sm">{f.label}</span>
                  <span className="font-mono-plex font-bold text-ec-emerald">{f.weight}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-4 font-mono-plex">
              * Score factors and weights apply to all account types. No manual editing of scores is permitted.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function VerificationEcosystem() {
  return (
    <section id="verify" className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-10 mb-14">
        <div className="lg:col-span-6">
          <div className="eyebrow">Verification ecosystem</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            One ID. Three ways to verify.
          </h2>
        </div>
        <p className="lg:col-span-5 lg:col-start-8 text-ec-ink-soft self-end leading-relaxed">
          Anyone — employer, partner, university or visa officer — can verify a passport without
          calling references or chasing PDFs.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-0 border border-ec-line">
        <VerifyMode
          icon={ScanLine}
          title="QR Scan"
          desc="Scan the QR on any EthicCred certificate or business card to see the full verified record."
          step="01"
        />
        <VerifyMode
          icon={Globe2}
          title="EthicCred ID lookup"
          desc="Paste any AAYY11YYXX-format ID at /verify to retrieve the live passport."
          step="02"
          border
        />
        <VerifyMode
          icon={Lock}
          title="Verification URL"
          desc="Every certificate has a permanent, signed verify URL with full audit history."
          step="03"
        />
      </div>
    </section>
  );
}

function VerifyMode({ icon: Icon, title, desc, step, border }) {
  return (
    <div className={`p-8 hover:bg-ec-soft transition-colors ${border ? "md:border-x md:border-ec-line" : ""}`}>
      <div className="flex items-center justify-between">
        <Icon className="w-6 h-6 text-ec-emerald" />
        <span className="font-mono-plex text-xs text-ec-ink-soft">{step}</span>
      </div>
      <h3 className="font-display text-xl font-bold mt-6">{title}</h3>
      <p className="text-sm text-ec-ink-soft mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function InstitutionsSection() {
  return (
    <section className="bg-ec-soft border-y border-ec-line">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="eyebrow">For educational institutions</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            Graduate passports, not paper degrees.
          </h2>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            Issue verified credentials directly to students' EthicCred passports — degrees,
            internships, conduct certificates and skill badges that follow them across every
            border.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6">
            <Stat n="900+" l="Institutions" />
            <Stat n="1.2M" l="Credentials" />
            <Stat n="48" l="Countries" />
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/institutions" data-testid="landing-institutions-link">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2">Learn more <ArrowUpRight className="w-4 h-4" /></Button>
            </Link>
            <Link to="/register?role=institution" data-testid="landing-institution-register">
              <Button variant="outline" className="rounded-sm border-ec-ink/20">Register your Institute</Button>
            </Link>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4"
        >
          {[
            { i: GraduationCap, t: "Degree Certificates" },
            { i: Award, t: "Conduct & Character" },
            { i: Briefcase, t: "Internship Records" },
            { i: Building2, t: "Placement Cell" },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-ec-line p-6 hover:-translate-y-1 transition-transform">
              <c.i className="w-6 h-6 text-ec-emerald" />
              <div className="font-display font-semibold mt-4">{c.t}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function GigWorkers() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-7">
          <div className="eyebrow">For freelancers, gig workers & creators</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            A reputation that travels with you.
          </h2>
        </div>
        <p className="lg:col-span-5 text-ec-ink-soft leading-relaxed">
          Stop rebuilding your reputation on every platform. Verified client recommendations,
          delivered work records and skills — all on one shareable Professional Passport.
        </p>
      </div>
      <div className="grid sm:grid-cols-3 gap-0 border border-ec-line">
        <FeatBlock title="Client-issued reviews" body="Clients sign recommendations directly to your passport. No fake reviews. No platform lock-in." />
        <FeatBlock title="Verified delivery record" body="Every completed project is logged with proof, duration, and a verifiable signature." border />
        <FeatBlock title="QR profile sharing" body="One scannable code that opens your portfolio + verified credibility score." />
      </div>
    </section>
  );
}

function FeatBlock({ title, body, border }) {
  return (
    <div className={`p-8 ${border ? "sm:border-x sm:border-ec-line border-y sm:border-y-0 border-ec-line" : ""}`}>
      <h3 className="font-display text-xl font-bold">{title}</h3>
      <p className="text-sm text-ec-ink-soft mt-3 leading-relaxed">{body}</p>
    </div>
  );
}

function Testimonials() {
  const tts = [
    {
      q: "We replaced our entire third-party background-check vendor with EthicCred in 30 days. Hires moved 41% faster, and our compliance team finally sleeps.",
      n: "Sandra K.",
      r: "Head of People · TalentBank",
    },
    {
      q: "My EthicCred passport landed me a role across two continents without a single reference call. The recruiter just scanned the QR.",
      n: "Arjun M.",
      r: "Senior Engineer · Remote",
    },
    {
      q: "We issue 30,000 graduate credentials a year. EthicCred turned a six-week process into a 30-second one.",
      n: "Prof. Elena R.",
      r: "Dean · Tech Institute Lisbon",
    },
  ];
  return (
    <section className="bg-ec-soft border-y border-ec-line">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="eyebrow">Testimonials</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3 max-w-2xl">
          The infrastructure trust runs on.
        </h2>
        <div className="grid md:grid-cols-3 gap-0 border border-ec-line mt-12">
          {tts.map((t, i) => (
            <div
              key={t.n}
              className={`bg-white p-8 ${i < tts.length - 1 ? "md:border-r border-ec-line border-b md:border-b-0" : ""}`}
            >
              <div className="flex gap-1 text-ec-amber">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-4 h-4 fill-current" />
                ))}
              </div>
              <p className="text-ec-ink mt-5 leading-relaxed">"{t.q}"</p>
              <div className="mt-6">
                <div className="font-display font-bold">{t.n}</div>
                <div className="eyebrow mt-1">{t.r}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const faqs = [
    { q: "Is EthicCred free for professionals?", a: "Yes. The Free Professional plan includes a Passport, EthicCred ID, Credibility Score and unlimited verified records. Premium plans unlock advanced analytics, custom domains and priority support." },
    { q: "Can a record be faked or edited?", a: "No. Records are issued and signed by verified organizations and institutions. Manual score editing is technically impossible — the engine computes everything from signed inputs only." },
    { q: "What happens if I leave my employer?", a: "Nothing. Your verified record stays on your passport for life. The employer cannot remove a record they have already signed." },
    { q: "How do organizations join?", a: "Sign up as an Organization, complete domain + KYC verification, and start issuing certificates the same day." },
    { q: "Is my data private?", a: "You control visibility per record. Set your passport public, private, or share-by-link. Every verification is logged with full consent receipts." },
  ];
  return (
    <section className="max-w-4xl mx-auto px-6 sm:px-10 py-24">
      <div className="text-center">
        <div className="eyebrow">FAQ</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
          Frequently asked.
        </h2>
      </div>
      <Accordion type="single" collapsible className="mt-10 border border-ec-line bg-white" data-testid="faq-accordion">
        {faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-ec-line last:border-b-0 px-6">
            <AccordionTrigger className="text-left font-display font-semibold text-lg py-5">
              {f.q}
            </AccordionTrigger>
            <AccordionContent className="text-ec-ink-soft pb-5 leading-relaxed">
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-16">
      <div className="relative bg-ec-ink text-white p-12 sm:p-16 overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 border border-white/10 rounded-full" />
        <div className="absolute -right-40 -bottom-40 w-[28rem] h-[28rem] border border-white/10 rounded-full" />
        <div className="relative grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow text-zinc-400">Get started</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Your career story is worth verifying.
            </h2>
            <p className="text-zinc-400 mt-4 max-w-xl">
              Free forever for professionals. Claim your global EthicCred ID in 60 seconds.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <Link to="/register" data-testid="cta-banner-register">
              <Button className="rounded-sm bg-white text-ec-ink hover:bg-zinc-200 h-12 px-6 gap-2">
                Claim my Passport <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ====================== NEW SECTIONS ======================

function CareerJourney() {
  const stages = [
    { stage: "Student", icon: GraduationCap, points: "+150 Education" },
    { stage: "Intern", icon: Briefcase, points: "+50 Internship" },
    { stage: "Volunteer", icon: Users, points: "+50 Volunteer" },
    { stage: "Employee", icon: Briefcase, points: "+300 Employment" },
    { stage: "Manager", icon: ShieldCheck, points: "+100 Leadership" },
    { stage: "Founder", icon: Sparkles, points: "+250 Conduct" },
    { stage: "Mentor", icon: Award, points: "+100 Recommendations" },
  ];
  return (
    <section id="journey" className="max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="career-journey-section">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-6">
          <div className="eyebrow">Your career journey</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            One passport. Every stage of your career.
          </h2>
        </div>
        <p className="lg:col-span-5 lg:col-start-8 text-ec-ink-soft self-end leading-relaxed">
          From your first internship to your founder chapter — every verified moment adds to your
          lifetime EthicCred Score.
        </p>
      </div>
      <div className="relative">
        <div className="hidden md:block absolute top-9 left-0 right-0 h-[2px] bg-ec-line z-0" />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 relative z-10">
          {stages.map((s, i) => (
            <div key={s.stage} className="text-center bg-ec-bg pt-4">
              <div className="mx-auto w-12 h-12 bg-white border-2 border-ec-ink flex items-center justify-center">
                <s.icon className="w-5 h-5 text-ec-ink" />
              </div>
              <div className="font-display font-bold mt-3">{s.stage}</div>
              <div className="font-mono-plex text-[10px] text-ec-emerald mt-1">{s.points}</div>
              {i < stages.length - 1 && (
                <div className="hidden md:block text-ec-ink-soft text-xs mt-1 font-mono-plex">→</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScoreBreakdownSection() {
  const breakdown = [
    { label: "Employment", v: 300 },
    { label: "Conduct", v: 250 },
    { label: "Education", v: 150 },
    { label: "Recommendations", v: 100 },
    { label: "Skills", v: 100 },
    { label: "Internships", v: 50 },
    { label: "Volunteer", v: 50 },
    { label: "Training", v: 50 },
  ];
  return (
    <section id="score" className="bg-ec-ink text-white" data-testid="score-breakdown-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow text-zinc-400">How the EthicCred Score works</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              0–1000. Earned. Never edited.
            </h2>
            <p className="text-zinc-400 mt-5 leading-relaxed">
              Every verified record contributes points. The maximum score is 1000. No manual score
              edits. Ever.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {["Starter", "Bronze", "Silver", "Gold", "Platinum", "Diamond", "Elite"].map((t) => (
                <TierBadge key={t} tier={t} />
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
            {breakdown.map((b) => (
              <div key={b.label} className="bg-ec-ink p-6 flex items-center justify-between">
                <span className="text-sm">{b.label}</span>
                <span className="font-display font-black text-2xl tracking-tighter text-ec-emerald">+{b.v}</span>
              </div>
            ))}
            <div className="bg-ec-emerald text-white p-6 flex items-center justify-between col-span-2">
              <span className="font-bold">MAXIMUM SCORE</span>
              <span className="font-display font-black text-3xl tracking-tighter">1000</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PublicPassportDemo() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="public-passport-demo">
      <div className="text-center mb-12">
        <div className="eyebrow">Public passport · live preview</div>
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
          What a verified passport looks like.
        </h2>
      </div>
      <div className="bg-ec-soft border border-ec-ink/15 p-10 noise-overlay relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04] text-[140px] font-display font-black flex items-center justify-center select-none">
          ETHICCRED
        </div>
        <div className="relative grid lg:grid-cols-3 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 text-ec-ink">
              <div className="w-7 h-7 bg-ec-ink text-white flex items-center justify-center">
                <ShieldCheck className="w-3.5 h-3.5" />
              </div>
              <span className="eyebrow text-ec-ink">Professional Passport</span>
            </div>
            <h3 className="font-display text-3xl font-black mt-3">A******* T********</h3>
            <p className="text-ec-ink-soft text-sm mt-1">Head of Engineering · S** P****</p>
            <div className="mt-5 inline-block">
              <TierBadge tier="Gold" size="lg" />
            </div>
            <div className="mt-3 font-mono-plex font-bold tracking-wider">AAYY11YYXX</div>
          </div>
          <div className="grid grid-cols-2 gap-6 lg:border-x lg:border-dashed lg:border-ec-ink/15 lg:px-10 py-2">
            <DemoRow label="Score" v="785" />
            <DemoRow label="Trust Tier" v="Gold" />
            <DemoRow label="Verified Records" v="22" />
            <DemoRow label="Recommendations" v="8" />
            <DemoRow label="Conduct Certs" v="5" />
            <DemoRow label="Top" v="6% globally" />
          </div>
          <div>
            <div className="eyebrow mb-3">Badges</div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { l: "Pro", color: "bg-ec-emerald" },
                { l: "Emp", color: "bg-ec-emerald" },
                { l: "Mentor", color: "bg-ec-emerald" },
                { l: "Trainer", color: "bg-ec-emerald" },
                { l: "Int", color: "bg-zinc-300" },
                { l: "Vol", color: "bg-ec-emerald" },
                { l: "Stu", color: "bg-ec-emerald" },
                { l: "Fdr", color: "bg-zinc-300" },
              ].map((b) => (
                <div key={b.l} className="aspect-square bg-white border border-ec-line flex items-center justify-center text-[10px] font-bold uppercase tracking-wider">
                  <span className={`w-1.5 h-1.5 ${b.color} rounded-full mr-1`} /> {b.l}
                </div>
              ))}
            </div>
            <Link to="/verify" data-testid="public-passport-demo-cta">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 mt-6 w-full gap-2">
                Try the public verifier <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function DemoRow({ label, v }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="font-display font-black text-2xl mt-1 tracking-tight">{v}</div>
    </div>
  );
}
